
import React, { createContext, useState, useContext, ReactNode } from 'react';
import { ThemeProvider, createTheme, CssBaseline, GlobalStyles } from '@mui/material';
import { paletteDark, paletteLight } from '../../theme/palette';
import { typography } from '../../theme/typography';

interface ThemeContextType {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const themeLight = createTheme({
  palette: paletteLight,
  typography,
});

const themeDark = createTheme({
  palette: paletteDark,
  typography,
});

export const ThemeProviderWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>('light');

  // Select the appropriate theme based on the current mode
  const theme = mode === 'light' ? themeLight : themeDark;

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />   
        <GlobalStyles
          styles={{
            "*": {
              scrollbarWidth: "thin", // Firefox
              // Color: `${theme.palette.primary.main} ${theme.palette.background.paper}`, //  thumb and track color for Firefox
            },
            "*::-webkit-": {
              width: "1px", // Width of the  for Webkit browsers
            },
            "*::-webkit--track": {
              background: theme.palette.background.paper, // Track background color
            },
            "*::-webkit--thumb": {
              backgroundColor: theme.palette.primary.main, // Thumb color
              borderRadius: "10px", // Rounded edges for the thumb
            },
          }}
        />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProviderWrapper');
  }
  return context;
};
